'use client';
import styled from '@benzinga/themetron';
import { SearchTool } from './SearchBarTool';
import React, { ClipboardEvent, MouseEvent } from 'react';
import { ExpressionToken, IndexedExpression, SearchBarProps, SearchBarState, TokenizedExpression } from '../SearchBar';
import { copyTextToClipboard, pasteTextFromClipboard } from '@benzinga/frontend-utils';
import { safeJsonParse } from '@benzinga/safe-await';
import { isArray } from '@benzinga/utils';
import { isNoCommaString } from '../../Modules';
import { SearchItem } from '@benzinga/search-modules';
import { genExistingSearchItems } from '../existingItems';
import { useAutocomplete } from '../../hooks';
import { Tooltip, message } from 'antd';
import { getEndOfGroup } from '../group';

import { ActiveBallotLight, ActiveCopy } from '@benzinga/themed-icons';

export const CLIPBOARD_TOOL = 'clipboard' as const;

export interface ClipboardTool extends SearchTool {
  id: typeof CLIPBOARD_TOOL;
}

const ClipboardIcon: React.FC<{
  state: SearchBarState;
  setState: React.Dispatch<React.SetStateAction<SearchBarState>>;
  props: SearchBarProps;
}> = ({ props, setState, state }) => {
  const autocompleteModules = React.useMemo(
    () => state.toggledModules.filter(module => module.enabled).map(module => module.autocomplete),
    [state.toggledModules],
  );
  const { search: pasteItemSearch } = useAutocomplete({
    autocompleteModules,
    queryLimit: 1,
  });
  const getModule = React.useCallback(
    (key: string) => state.toggledModules.find(module => module.id === key),
    [state.toggledModules],
  );

  const existingSearchItems = React.useMemo<ExpressionToken<unknown>[]>(
    () => genExistingSearchItems({ expressions: state.parsedExpression, index: state.inputIndex }),
    [state.parsedExpression, state.inputIndex],
  );

  const pasteHandler = React.useCallback(
    async (evt?: ClipboardEvent<HTMLInputElement>) => {
      setState(old => ({ ...old, inFocus: false, isOpen: false, orSelected: false }));
      evt?.preventDefault();
      let clipData = evt?.clipboardData.getData('text');
      if (!clipData) {
        clipData = await pasteTextFromClipboard();
      }
      if (!clipData) return;
      const tokens = safeJsonParse<TokenizedExpression[]>(clipData);

      const expr: TokenizedExpression[] = [];

      if (!tokens.err && isArray(tokens.ok)) {
        //Complex paste, since we got an object, parse object and try to match with modules
        expr.push(
          ...tokens.ok.flatMap<TokenizedExpression>(token => {
            switch (token.operator) {
              case 'expression': {
                const parsedToken = getModule(token.moduleId)?.clipboard.onPasteJSON(token);
                return parsedToken ? [{ operator: 'expression', ...parsedToken }, { operator: 'position' }] : [];
              }
              default:
                return token;
            }
          }),
        );
      } else {
        //Simple paste, assuming comma separated string.
        const pasteStrings = clipData.split(/[\n,]+/).flatMap(str => {
          const trimStr = str.trim();
          return isNoCommaString(trimStr) && trimStr.length > 0 ? [trimStr] : [];
        });

        for (const str of pasteStrings) {
          //Try to match each string to a module.
          let token = state.toggledModules.reduce<SearchItem | undefined>((t, module) => {
            if (!t) {
              const result = module.clipboard.onPasteString?.(str);
              if (
                !existingSearchItems.some(
                  existingItem => existingItem.moduleId === result?.moduleId && existingItem.id === result?.id,
                )
              ) {
                t = result;
              }
            }
            return t;
          }, undefined);

          //If no module accepts the string as its own, do a general search and pick the top result.
          if (!token) {
            token = await pasteItemSearch(str, existingSearchItems).then(items => items?.[0]);
          }
          if (token) {
            expr.push({ operator: 'expression', ...token }, { operator: 'position' });
          }
        }
      }

      if (expr.length !== 0) {
        // remove extra position element from the end
        state.parsedExpression.splice(state.inputIndex, 0, ...expr.slice(0, -1));
        setState(old => ({ ...old, inputIndex: old.inputIndex + expr.length - 1 }));
        props.onExpressionUpdate(state.parsedExpression);
      }
    },
    [
      existingSearchItems,
      getModule,
      pasteItemSearch,
      props,
      setState,
      state.inputIndex,
      state.parsedExpression,
      state.toggledModules,
    ],
  );

  const copyGroup = React.useCallback(
    (groupStart: IndexedExpression) => {
      setState(s => ({
        ...s,
        inFocus: false,
        isOpen: false,
        orSelected: false,
      }));
      const groupEnd = getEndOfGroup(groupStart);
      const expr = groupStart.expressions.slice(groupStart.index, groupEnd + 1);

      let copiedString = '';

      if (
        expr.every((token, index, arr) => {
          const module = token.operator === 'expression' ? getModule(token.moduleId) : undefined;
          return (
            //ignore initial and final grouping
            index === 0 ||
            index === arr.length - 1 ||
            token.operator === 'or' ||
            token.operator === 'position' ||
            (token.operator === 'expression' && (!module || module.clipboard.onCopyString))
          );
        })
      ) {
        // simple copy
        copiedString = expr
          .reduce((str, token) => {
            if (token.operator === 'expression') {
              const tokenStr = getModule(token.moduleId)?.clipboard.onCopyString?.(token);
              if (tokenStr) {
                return str + ',' + tokenStr;
              }
            }
            return str;
          }, '')
          .slice(1); //remove extra , from the start of the string
      } else {
        // complex copy
        const copiedData = expr.flatMap<TokenizedExpression>(token => {
          switch (token.operator) {
            case 'expression': {
              const copiedToken = getModule(token.moduleId)?.clipboard.onCopyJSON(token);
              return copiedToken ? copiedToken.map(token => ({ operator: 'expression', ...token })) : [];
            }
            case 'position':
              return [];
            default:
              return [token];
          }
        });

        copiedString = JSON.stringify(copiedData);
      }
      return copyTextToClipboard(copiedString);
    },
    [getModule, setState],
  );

  const copyClickHandler = React.useCallback(
    (e: MouseEvent<Element>) => {
      e.stopPropagation();

      setState(old => ({ ...old, isOpen: false }));
      let copiedExpression: TokenizedExpression[] = state.parsedExpression;

      // pad it with position if needed
      // so that the actual expression always starts from index 1
      if (state.parsedExpression[0].operator !== 'position') {
        copiedExpression = [
          {
            operator: 'position',
          },
          ...copiedExpression,
          {
            operator: 'position',
          },
        ];
      }

      // if braces don't already exist around the whole expression, pad the expression
      // padding position again is necessary so that our braces always start from index 1
      if (
        state.parsedExpression[1].operator !== 'group_start' ||
        getEndOfGroup({
          expressions: copiedExpression,
          index: 1,
        }) !==
          copiedExpression.length - 2
      ) {
        copiedExpression = [
          {
            operator: 'position',
          },
          {
            groupIndex: 9999,
            operator: 'group_start',
          },
          ...copiedExpression,
          {
            groupIndex: 9999,
            operator: 'group_end',
          },
          {
            operator: 'position',
          },
        ];
      }

      // group to be copied starts from index 1
      copyGroup({ expressions: copiedExpression, index: 1 });

      setState(s => ({
        ...s,
        copied: true,
        isFocus: false,
        isOpen: false,
      }));

      setTimeout(() => {
        setState(s => ({
          ...s,
          copied: false,
        }));
      }, 1000);
      message.success('Successfully Search copied to clipboard');
    },
    [state.parsedExpression, copyGroup, setState],
  );

  if (state.isOpen) {
    return (
      <Tooltip placement={'topRight'} showArrow={false} title={'Paste'}>
        <PasteIconActive onClick={() => pasteHandler()} />
      </Tooltip>
    );
  } else if (state.parsedExpression.length > 1) {
    return (
      <Tooltip
        defaultOpen={state.copied}
        id="copyButtonTooltip"
        placement={'topRight'}
        showArrow={false}
        title={state.copied ? 'Copied!' : 'Copy'}
      >
        <CopyIconActive onClick={copyClickHandler} />
      </Tooltip>
    );
  } else {
    return (
      <Tooltip
        id="copyButtonTooltip"
        placement={'topRight'}
        showArrow={false}
        title={state.copied ? 'Copied!' : 'Copy'}
      >
        <CopyIconInactive />
      </Tooltip>
    );
  }
};

export const createClipboardTool = (): ClipboardTool => {
  return {
    getTool: (searchBarProps: SearchBarProps, searchBarState, setSearchBarState) => (
      <ClipboardIcon key="clipboard-icon" props={searchBarProps} setState={setSearchBarState} state={searchBarState} />
    ),
    id: CLIPBOARD_TOOL,
  };
};

export function isClipboardTool(tool: SearchTool): tool is ClipboardTool {
  return tool.id === CLIPBOARD_TOOL;
}

export const useClipboardTool = (): ClipboardTool => {
  return React.useMemo(() => createClipboardTool(), []);
};

const PasteIconActive = styled(ActiveBallotLight)`
  fill: ${props => props.theme.colors.foregroundInactive};
  stroke: ${props => props.theme.colors.foregroundInactive};
  cursor: pointer;
  margin: 8px;
`;

const CopyIconActive = styled(ActiveCopy)`
  fill: ${props => props.theme.colors.foregroundActive};
  cursor: pointer;
  margin: 8px;
`;

const CopyIconInactive = styled(ActiveCopy)`
  fill: ${props => props.theme.colors.foregroundInactive};
  margin: 8px;
`;
